/**
 * AI background removal — BiRefNet matte from the `catalogRemoveImageBg`
 * Cloud Function, composed locally over the ORIGINAL pixels (colors and
 * shapes stay untouched; only an alpha channel is added).
 *
 * Why server-side: BiRefNet (MIT) is the only tested model that cleanly
 * preserves white/light areas INSIDE products, but it cannot run in the
 * browser — its WebGPU graph needs 17 storage buffers (many GPUs cap at 16)
 * and its WASM footprint exceeds the 4GB wasm memory limit (std::bad_alloc).
 * Browser-sized models (ISNet) were tested and eat white-ish label areas.
 * On the server every admin gets an identical, deterministic result.
 *
 * License note: @imgly/background-removal (AGPL) and BRIA RMBG
 * (non-commercial) are license-incompatible — do not switch to them.
 */
import { callFunction } from '@core/firebase/functions.js'
import { removeWhiteBackground } from './imageBgRemoval.js'

// Matte confidence bands (0-255)
const AI_FG = 128 // ≥ this: confident product
const AI_UNCERTAIN = 25 // ≥ this: not confidently background

/**
 * Remove the background of an image with the neural model.
 *
 * @param {ImageData} source - input pixels (not mutated)
 * @param {{ onProgress?: (e: { stage: 'download'|'run', pct?: number }) => void }} [options]
 * @returns {Promise<{ imageData: ImageData, matte: Uint8Array, removedRatio: number }>}
 *   imageData - original pixels with the fused alpha matte applied
 *   matte - per-pixel alpha (0-255) aligned with `source` dimensions
 */
export async function removeBackgroundAI(source, options = {}) {
  const { onProgress } = options
  onProgress?.({ stage: 'run' })

  const imageBase64 = imageDataToJpegDataUrl(source)
  const result = await callFunction('catalogRemoveImageBg', { imageBase64 }, { timeout: 300000 })
  if (!result?.matteBase64) {
    throw new Error('AI matte missing from server response')
  }
  const matte = await decodeMatteToSize(result.matteBase64, source.width, source.height)
  return fuseAndCompose(source, matte)
}

/**
 * Encode source pixels as a JPEG data URL for the callable payload.
 * Flattened onto white (like the catalog's product shots) so re-processing
 * an already-transparent PNG behaves the same as the original photo.
 */
function imageDataToJpegDataUrl(imageData) {
  const raw = document.createElement('canvas')
  raw.width = imageData.width
  raw.height = imageData.height
  raw.getContext('2d').putImageData(imageData, 0, 0)

  const flat = document.createElement('canvas')
  flat.width = imageData.width
  flat.height = imageData.height
  const ctx = flat.getContext('2d')
  ctx.fillStyle = '#ffffff'
  ctx.fillRect(0, 0, flat.width, flat.height)
  ctx.drawImage(raw, 0, 0)
  return flat.toDataURL('image/jpeg', 0.92)
}

/**
 * Decode the server's grayscale matte PNG and rescale it to the source size
 * (canvas bilinear — keeps the edge smooth). Returns one alpha byte per pixel.
 */
async function decodeMatteToSize(matteBase64, width, height) {
  const img = new Image()
  img.src = `data:image/png;base64,${matteBase64}`
  await img.decode()

  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const ctx = canvas.getContext('2d', { willReadFrequently: true })
  ctx.drawImage(img, 0, 0, width, height)
  const data = ctx.getImageData(0, 0, width, height).data

  const matte = new Uint8Array(width * height)
  for (let i = 0; i < matte.length; i++) {
    matte[i] = data[i * 4]
  }
  return matte
}

/**
 * Fuse the AI matte with the classic flood-fill engine and compose the final
 * cutout. The two engines have complementary blind spots:
 * - classic wrongly removes white PRODUCT EDGES touching the background —
 *   the AI is confident they are product;
 * - the AI may be unsure about white-ish LABEL AREAS inside the product —
 *   the classic engine keeps them when they are color-enclosed.
 * A pixel is product when the AI is confident, OR when the AI is merely
 * uncertain (not confident-background) and the classic engine kept it.
 * Confidently-background pixels (true background, shadows) stay removed.
 * The union is then solidified (enclosed pockets → opaque) with a 2px
 * anti-aliased rim from the AI matte.
 *
 * Exported for testability — `matte` must be `source.width * source.height`.
 */
export function fuseAndCompose(source, aiMatte) {
  const width = source.width
  const height = source.height
  const totalPixels = width * height

  const classic = removeWhiteBackground(source, { tolerance: 30 })
  const classicData = classic.imageData.data

  // ---- fused foreground mask ----
  const fg = new Uint8Array(totalPixels)
  for (let i = 0; i < totalPixels; i++) {
    if (aiMatte[i] >= AI_FG) fg[i] = 1
    else if (aiMatte[i] >= AI_UNCERTAIN && classicData[i * 4 + 3] >= 128) fg[i] = 1
  }

  // ---- solidify: background = border-connected non-fg only ----
  // Enclosed pockets (mid-confidence dips inside the product) become opaque.
  const outside = new Uint8Array(totalPixels)
  const stack = new Int32Array(totalPixels)
  let sp = 0
  const seed = (i) => {
    if (!fg[i] && !outside[i]) {
      outside[i] = 1
      stack[sp++] = i
    }
  }
  for (let x = 0; x < width; x++) {
    seed(x)
    seed((height - 1) * width + x)
  }
  for (let y = 1; y < height - 1; y++) {
    seed(y * width)
    seed(y * width + width - 1)
  }
  while (sp > 0) {
    const i = stack[--sp]
    const x = i % width
    if (x > 0) seed(i - 1)
    if (x < width - 1) seed(i + 1)
    if (i >= width) seed(i - width)
    if (i < totalPixels - width) seed(i + width)
  }

  // ---- 2px dilation of fg for the anti-aliased rim ----
  const dilH = new Uint8Array(totalPixels)
  for (let y = 0; y < height; y++) {
    const row = y * width
    for (let x = 0; x < width; x++) {
      const i = row + x
      if (
        fg[i] ||
        (x > 0 && fg[i - 1]) ||
        (x > 1 && fg[i - 2]) ||
        (x < width - 1 && fg[i + 1]) ||
        (x < width - 2 && fg[i + 2])
      ) {
        dilH[i] = 1
      }
    }
  }
  const rim = new Uint8Array(totalPixels)
  for (let y = 0; y < height; y++) {
    const row = y * width
    for (let x = 0; x < width; x++) {
      const i = row + x
      if (
        dilH[i] ||
        (y > 0 && dilH[i - width]) ||
        (y > 1 && dilH[i - 2 * width]) ||
        (y < height - 1 && dilH[i + width]) ||
        (y < height - 2 && dilH[i + 2 * width])
      ) {
        rim[i] = 1
      }
    }
  }

  // ---- compose: ORIGINAL pixels, alpha from the fused mask ----
  const out = new ImageData(new Uint8ClampedArray(source.data), width, height)
  const data = out.data
  const matte = new Uint8Array(totalPixels)
  let cleared = 0
  for (let i = 0; i < totalPixels; i++) {
    const o = i * 4
    let a = !outside[i] ? 255 : rim[i] ? aiMatte[i] : 0
    if (a > data[o + 3]) a = data[o + 3] // respect source transparency
    if (a < 6) {
      // fully background: zero out so no hidden data ships in the PNG
      data[o] = 0
      data[o + 1] = 0
      data[o + 2] = 0
      data[o + 3] = 0
      if (source.data[o + 3] > 0) cleared++
      continue
    }
    data[o + 3] = a
    matte[i] = a
  }

  return {
    imageData: out,
    matte,
    removedRatio: totalPixels > 0 ? cleared / totalPixels : 0,
  }
}

/**
 * Binarize an AI matte into the foreground mask expected by
 * `composeStickerOutline` (imageBgRemoval.js).
 * @param {Uint8Array} matte
 * @param {number} [threshold=128]
 * @returns {Uint8Array}
 */
export function matteToForegroundMask(matte, threshold = 128) {
  const mask = new Uint8Array(matte.length)
  for (let i = 0; i < matte.length; i++) {
    if (matte[i] >= threshold) mask[i] = 1
  }
  return mask
}
